import React, {useEffect, useRef, useState} from 'react'
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Typography,
} from '@material-ui/core'
import {createTheme, ThemeProvider} from '@material-ui/core/styles'
import RefreshIcon from '@material-ui/icons/Refresh'
import {InkPoint, SignatureInk, renderSignature} from './signatureInk'
import {
  Handedness,
  SignatureTabletProfile,
  hidRequestFilters,
  messageForHidError,
  selectTabletProfile,
} from '../tablets/g430s'
import {SignatureTabletSession, TabletHidDevice} from '../tablets/g430sHid'
import {isTabletCaptureRequested} from '../tablets/tabletQuery'

const theme = createTheme({
  palette: {
    primary: {
      main: '#003172',
    },
    secondary: {
      main: '#D45041',
    },
  },
})

const HANDEDNESS_KEY = 'spsignature.tablet.handedness'

type HidNavigator = Navigator & {
  hid?: {
    getDevices(): Promise<TabletHidDevice[]>
    requestDevice(options: {
      filters: ReturnType<typeof hidRequestFilters>
    }): Promise<TabletHidDevice[]>
    addEventListener(
      type: 'disconnect',
      listener: (event: {device: TabletHidDevice}) => void,
    ): void
    removeEventListener(
      type: 'disconnect',
      listener: (event: {device: TabletHidDevice}) => void,
    ): void
  }
}

function readHandedness(): Handedness {
  try {
    return window.localStorage.getItem(HANDEDNESS_KEY) === 'left'
      ? 'left'
      : 'right'
  } catch (error) {
    return 'right'
  }
}

function pointerPressure(event: PointerEvent): number | null {
  if (event.pointerType === 'pen') return event.pressure
  if (
    event.pointerType === 'touch' &&
    event.pressure > 0 &&
    Math.abs(event.pressure - 0.5) > 0.02
  ) {
    return event.pressure
  }
  return null
}

function isPenContact(event: PointerEvent): boolean {
  if ((event.buttons & 32) !== 0 || event.button === 5) return false
  return (event.buttons & 1) === 1 || event.pressure > 0
}

const FreeHandSign = (props: {getData: (data: string) => void}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const inkRef = useRef(new SignatureInk())
  const getDataRef = useRef(props.getData)
  const sessionRef = useRef<SignatureTabletSession | null>(null)
  const deviceRef = useRef<TabletHidDevice | null>(null)
  const applyTabletPointRef = useRef<
    (point: {
      phase: 'down' | 'move' | 'up'
      x: number
      y: number
      pressure: number
      time: number
    }) => void
  >(() => undefined)
  const activePointer = useRef<number | null>(null)
  const lastPenAt = useRef(0)
  const [handedness, setHandedness] = useState<Handedness>(readHandedness)
  const handednessRef = useRef(handedness)
  const [hidSupported, setHidSupported] = useState(false)
  const tabletRequested = isTabletCaptureRequested(window.location)
  const [profile, setProfile] = useState<SignatureTabletProfile | null>(null)
  const [connecting, setConnecting] = useState(false)
  const [hidError, setHidError] = useState<string | null>(null)
  const [penSeen, setPenSeen] = useState(false)

  getDataRef.current = props.getData
  handednessRef.current = handedness

  const paint = () => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    const rect = canvas.getBoundingClientRect()
    renderSignature(
      ctx,
      rect.width || canvas.clientWidth || 1,
      rect.height || canvas.clientHeight || 1,
      inkRef.current.allStrokes(),
    )
  }

  const publish = () => {
    const canvas = canvasRef.current
    if (!canvas || inkRef.current.isEmpty) {
      getDataRef.current('')
      return
    }
    getDataRef.current(canvas.toDataURL('image/png'))
  }

  const applyTabletPoint = (point: {
    phase: 'down' | 'move' | 'up'
    x: number
    y: number
    pressure: number
    time: number
  }) => {
    const canvas = canvasRef.current
    if (!canvas) return
    if (point.phase === 'up') {
      if (inkRef.current.end()) {
        paint()
        publish()
      }
      return
    }
    const rect = canvas.getBoundingClientRect()
    const sample: InkPoint = {
      x: point.x * rect.width,
      y: point.y * rect.height,
      pressure: point.pressure,
      time: point.time,
    }
    if (point.phase === 'down') inkRef.current.begin(sample, 'pen')
    else inkRef.current.extend(sample)
    paint()
  }
  applyTabletPointRef.current = applyTabletPoint

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return undefined
    const resize = () => {
      const rect = canvas.getBoundingClientRect()
      if (rect.width < 2 || rect.height < 2) return
      const dpr = window.devicePixelRatio || 1
      const width = Math.max(1, Math.round(rect.width * dpr))
      const height = Math.max(1, Math.round(rect.height * dpr))
      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width
        canvas.height = height
      }
      paint()
    }
    resize()
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', resize)
      return () => window.removeEventListener('resize', resize)
    }
    const observer = new ResizeObserver(resize)
    observer.observe(canvas)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (!tabletRequested) {
      setHidSupported(false)
      return undefined
    }
    const hid = (navigator as HidNavigator).hid
    setHidSupported(Boolean(hid))
    let alive = true
    const session = new SignatureTabletSession({
      onConnection(next) {
        if (!alive) return
        setProfile(next)
        setHidError(null)
      },
      onPoint: point => applyTabletPointRef.current(point),
      onError(message) {
        if (alive) setHidError(message)
      },
      onClose() {
        deviceRef.current = null
        if (alive) setProfile(null)
      },
    })
    session.setHandedness(handednessRef.current)
    sessionRef.current = session

    const onDisconnect = (event: {device: TabletHidDevice}) => {
      if (event.device === deviceRef.current) {
        session.close()
      }
    }
    if (hid) {
      hid.addEventListener('disconnect', onDisconnect)
      hid
        .getDevices()
        .then(devices => {
          if (!alive) return
          const granted = devices.find(device =>
            selectTabletProfile(
              device.vendorId,
              device.productId,
              undefined,
              device.productName,
            ),
          )
          if (!granted) return
          deviceRef.current = granted
          return session.attach(granted)
        })
        .catch(error => {
          const message = messageForHidError(error)
          if (alive && message) setHidError(message)
        })
    }

    return () => {
      alive = false
      if (hid) hid.removeEventListener('disconnect', onDisconnect)
      session.close()
      sessionRef.current = null
    }
  }, [tabletRequested])

  useEffect(() => {
    sessionRef.current?.setHandedness(handedness)
    try {
      window.localStorage.setItem(HANDEDNESS_KEY, handedness)
    } catch (error) {
      // Storage can be blocked. The choice still applies to this page.
    }
  }, [handedness])

  const connectTablet = async () => {
    const hid = (navigator as HidNavigator).hid
    const session = sessionRef.current
    if (!tabletRequested || !hid || !session) return
    setConnecting(true)
    setHidError(null)
    try {
      const devices = await hid.requestDevice({filters: hidRequestFilters()})
      const selected = devices[0]
      if (!selected) return
      if (!session.identify(selected)) {
        setHidError('Cette tablette USB n’est pas une XPPen Star G430S.')
        return
      }
      if (deviceRef.current && deviceRef.current !== selected) {
        await session.close()
      }
      deviceRef.current = selected
      await session.attach(selected)
    } catch (error) {
      const message = messageForHidError(error)
      if (message) setHidError(message)
    } finally {
      setConnecting(false)
    }
  }

  const localSample = (event: PointerEvent): InkPoint | null => {
    const canvas = canvasRef.current
    if (!canvas) return null
    const rect = canvas.getBoundingClientRect()
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
      pressure: pointerPressure(event),
      time: event.timeStamp || Date.now(),
    }
  }

  const samplesFrom = (event: PointerEvent): PointerEvent[] => {
    const coalesced = event.getCoalescedEvents ? event.getCoalescedEvents() : []
    return coalesced.length > 0 ? coalesced : [event]
  }

  const onPointerDown = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (sessionRef.current?.connected) return
    if (event.pointerType === 'touch' && Date.now() - lastPenAt.current < 800) {
      event.preventDefault()
      return
    }
    if (event.pointerType === 'pen') {
      lastPenAt.current = Date.now()
      if (tabletRequested) setPenSeen(true)
      if (!isPenContact(event.nativeEvent)) return
    } else if (event.button !== 0) {
      return
    }
    event.preventDefault()
    activePointer.current = event.pointerId
    event.currentTarget.setPointerCapture(event.pointerId)
    const sample = localSample(event.nativeEvent)
    if (!sample) return
    inkRef.current.begin(
      sample,
      event.pointerType === 'mouse' ? 'pointer' : 'pen',
    )
    paint()
  }

  const onPointerMove = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (sessionRef.current?.connected) {
      if (activePointer.current === event.pointerId) {
        finishPointer(event.currentTarget)
      }
      return
    }
    if (event.pointerType === 'pen') {
      lastPenAt.current = Date.now()
      if (tabletRequested && !penSeen) setPenSeen(true)
    }
    if (activePointer.current !== event.pointerId) return
    if (event.pointerType === 'pen' && !isPenContact(event.nativeEvent)) {
      finishPointer(event.currentTarget)
      return
    }
    samplesFrom(event.nativeEvent).forEach(sampleEvent => {
      const sample = localSample(sampleEvent)
      if (sample) inkRef.current.extend(sample)
    })
    paint()
  }

  const finishPointer = (canvas: HTMLCanvasElement) => {
    if (activePointer.current == null) return
    if (canvas.hasPointerCapture(activePointer.current)) {
      canvas.releasePointerCapture(activePointer.current)
    }
    activePointer.current = null
    if (inkRef.current.end()) {
      paint()
      publish()
    }
  }

  const onPointerUp = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (activePointer.current !== event.pointerId) return
    if (sessionRef.current?.connected) {
      finishPointer(event.currentTarget)
      return
    }
    const trailing = localSample(event.nativeEvent)
    if (trailing) inkRef.current.extend(trailing)
    finishPointer(event.currentTarget)
  }

  const clear = () => {
    activePointer.current = null
    inkRef.current.clear()
    paint()
    getDataRef.current('')
  }

  const status = !tabletRequested
    ? ''
    : profile
    ? profile.displayName +
      ' connectée. Toute la surface 4×3 pouces correspond au cadre.'
    : hidError
    ? hidError
    : penSeen
    ? 'Stylet détecté. La pression du stylet est prise en compte.'
    : hidSupported
    ? 'Signez dans le cadre, ou recherchez la tablette pour utiliser toute sa surface.'
    : 'Ce navigateur ne peut pas rechercher la tablette. Signez dans le cadre avec la souris, le doigt ou le stylet.'

  return (
    <ThemeProvider theme={theme}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          marginTop: 30,
        }}>
        <Typography
          color="primary"
          style={{
            margin: '10px',
            userSelect: 'none',
            WebkitUserSelect: 'none',
          }}>
          Veuillez signez ici
        </Typography>
        {status ? (
          <Typography
            color="primary"
            variant="body2"
            style={{margin: '0 16px 10px', textAlign: 'center', maxWidth: 460}}>
            {status}
          </Typography>
        ) : null}

        <Box
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: 'min(420px, 92vw)',
            height: 'min(315px, 69vw)',
            background: '#fff',
            border: '4px solid #003172',
            borderRadius: 25,
            overflow: 'hidden',
          }}>
          <canvas
            ref={canvasRef}
            aria-label="Zone de signature"
            style={{
              width: '100%',
              height: '100%',
              touchAction: 'none',
              cursor: 'crosshair',
              borderRadius: 21,
            }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
          />
        </Box>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            justifyContent: 'center',
          }}>
          <IconButton
            color="primary"
            aria-label="Effacer la signature"
            onClick={clear}
            component="span">
            <RefreshIcon />
          </IconButton>
          {tabletRequested && hidSupported && !profile && (
            <Button
              color="primary"
              onClick={connectTablet}
              disabled={connecting}>
              Rechercher tablette graphique
            </Button>
          )}
          {tabletRequested && profile && (
            <FormControlLabel
              control={
                <Checkbox
                  color="primary"
                  checked={handedness === 'left'}
                  onChange={event =>
                    setHandedness(event.target.checked ? 'left' : 'right')
                  }
                />
              }
              label="Gaucher"
            />
          )}
        </div>
      </div>
    </ThemeProvider>
  )
}

export default FreeHandSign
